import { CheckCircle2, Satellite } from "lucide-react";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { LeadForm } from "@/components/site/LeadForm";
import { RoofMap } from "@/components/site/RoofMap";
import { track } from "@/lib/track";

// Stato dell'invio email della simulazione (FormSubmit)
const MailStatus = ({ status, error }) => {
  if (status === "sending") return <p data-testid="mail-status-sending" className="mt-4 text-sm text-slate-400">Invio in corso...</p>;
  if (status === "sent") return <p data-testid="mail-status-sent" className="mt-4 text-sm font-medium text-green-400">✅ Richiesta inviata! Ti contatteremo a breve.</p>;
  if (status === "error") return <p data-testid="mail-status-error" className="mt-4 text-sm text-rose-400">Invio non riuscito{error ? `: ${error}` : ""}. Puoi scriverci subito su WhatsApp.</p>;
  return null;
};
import { fmtKwp, fmtMoney, ZONE_LABEL } from "@/lib/apolloCalc";
import { buildSimulationMessage, buildWhatsAppLink } from "@/lib/whatsapp";

const RoofInfo = ({ result }) => {
  if (!result.roof)
    return <p data-testid="result-source" className="mt-5 text-xs text-slate-500">Tetto non disponibile da satellite: stima basata sulla zona.</p>;
  const r = result.roof;
  return (
    <div data-testid="result-roof" className="mt-5 border border-slate-800 p-4 text-sm text-slate-300">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-400"><Satellite className="h-4 w-4" /> Analisi satellitare Google Solar</p>
      <p className="mt-2">Superficie utile {r.max_area_m2.toLocaleString("it-IT")} m² · fino a {r.max_panels} pannelli · {r.sunshine_hours.toLocaleString("it-IT")} ore di sole/anno{r.imagery_year ? ` · immagini ${r.imagery_year}` : ""}</p>
      {result.capped && <p data-testid="result-capped" className="mt-1 text-xs text-slate-400">Per coprire tutto il consumo servirebbero {result.neededPanels} pannelli: abbiamo usato il massimo che il tetto può ospitare.</p>}
      {r.panels?.length > 0 && <RoofMap roof={r} count={result.panels} />}
    </div>
  );
};

const Metric = ({ label, value, sub, testId }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
    <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
    <p translate="no" data-testid={testId} className="mt-2 whitespace-nowrap font-display text-2xl font-semibold text-white sm:text-3xl">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
  </div>
);

export const ResultCard = ({ result, mailStatus = "idle", mailError = "" }) => {
  // Il link WhatsApp viene ricostruito con i valori reali di questa simulazione
  const waLink = buildWhatsAppLink(buildSimulationMessage(result));
  return (
    <div data-testid="calculator-result-card" className="fade-up space-y-6">
      <div className="rounded-3xl border border-slate-800 bg-[#111827] p-6 sm:p-8">
        <div data-testid="feasibility-badge" className="flex items-center justify-center gap-3 rounded-2xl bg-green-500 px-6 py-5 text-center text-white shadow-lg shadow-green-500/20">
          <CheckCircle2 className="h-7 w-7 shrink-0" />
          <span className="font-display text-2xl font-bold sm:text-3xl">OK – Fattibile al {result.feasibility}%</span>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4">
          <Metric label="Pannelli" value={result.panels} sub="moduli da 400 W" testId="result-panels" />
          <Metric label="Potenza" value={`${fmtKwp(result.kwp)} kWp`} sub={`≈ ${result.annualProduction.toLocaleString("it-IT")} kWh/anno`} testId="result-kwp" />
          <Metric label="Risparmio stimato" value={`${fmtMoney(result.savings, result.currency)}`} sub={`all'anno · ${result.price.toLocaleString("it-IT")} ${result.currency}/kWh`} testId="result-savings" />
          <Metric label="Zona rilevata" value={ZONE_LABEL[result.zone]} sub={result.city ? `${result.city} · ${result.yieldZone} kWh/kWp` : `Città non riconosciuta · ${result.yieldZone} kWh/kWp`} testId="result-zone" />
        </div>
        <RoofInfo result={result} />
        <p className="mt-4 text-sm text-slate-400">
          {result.buildingType} · consumo {result.consumptionEstimated ? "stimato" : "dichiarato"} {result.consumptionUsed.toLocaleString("it-IT")} kWh/anno · risparmio calcolato sull'energia prodotta e autoconsumata, stima indicativa
        </p>
        <a
          data-testid="calculator-whatsapp-cta-button"
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("click_whatsapp", { position: "risultato" })}
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl bg-[#25D366] px-6 py-4 text-base font-bold text-white shadow-lg transition-transform hover:scale-[1.01] hover:bg-[#1EBE5D] sm:text-lg"
        >
          <WhatsAppIcon className="h-6 w-6" /> Richiedi maggiori informazioni gratuite
        </a>
        <MailStatus status={mailStatus} error={mailError} />
      </div>
      <LeadForm result={result} />
    </div>
  );
};
